// Commons imports.
import { EMPTY_STRING } from '../../../NonReact/constants/Common'
import { FAILED, SUCCESS } from '../../../NonReact/constants/ResponseResultTypes'
import { StatusCodes } from '../../../NonReact/constants/StatusCodes'
import { RESTAPIError } from '../../../NonReact/common/API/REST/RESTAPIError'
import { redirectIfSessionExpired } from '../../../NonReact/utils/SessionUtils'

/**
 * @class API error data object.
 */
export class InternalAPIError extends Error {
    private readonly HTTPStatus: number
    private readonly JSONObject: object | null
    private readonly resultType: number
    private readonly errorMessage: string
    readonly name: string // Overridden Error name property;

    /**
     * Constructor.
     *
     * @param HTTPStatus HTTP status code.
     * @param resultType Akixi result type.
     * @param JSONObject JSON object from the response.
     * @param errorMessage Error message.
     */
    constructor(HTTPStatus: number, resultType: number, JSONObject: object | null, errorMessage: string) {
        super(errorMessage)
        this.HTTPStatus = HTTPStatus
        this.JSONObject = JSONObject
        this.resultType = resultType
        this.errorMessage = errorMessage
        this.name = 'InternalAPIError'
    }
}

/**
 * Processes HTTP response from API.
 * @param {Response} response HTTP response.
 */
export const processJSONResponse = async (response: Response, isErrorIgnored: boolean): Promise<object | Response> => {
    try {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const JSONResponse = await response.json()
        // eslint-disable-next-line
        const results = JSONResponse?.RequestResults
        // eslint-disable-next-line
        let result: any

        if (Array.isArray(results)) {
            // eslint-disable-next-line
            result = results[0].RequestResult
        } else {
            // In case of bug in the backend the response will not contain the result.
            throw new InternalAPIError(response.status, FAILED, null, 'No result has been found in the response.')
        }

        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        const resultType: number = result?.Type as number

        switch (resultType) {
            case SUCCESS:
                // Successful response.
                // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                return result.ResultData as object | Response
            default:
                // Error cases that have to be handled by calling logic.
                if (isErrorIgnored) {
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                    return result.ResultData as object | Response
                }

                throw new InternalAPIError(
                    response.status,
                    resultType,
                    result as OnlyNullable<object>,
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                    (result?.Error ?? EMPTY_STRING) as string
                )
        }
    } catch (error) {
        if ((error as Error).name === 'InternalAPIError') {
            throw error
        } else {
            // Wrap error so that we can handle that in application correctly.
            throw new InternalAPIError(response.status, FAILED, null, response.statusText)
        }
    }
}

/**
 * Prepares HTTP options for the request.
 * @param requestOptions HTTP request options.
 */
// eslint-disable-next-line
const prepareHTTPOptions = (requestOptions: any): any => {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    if (!requestOptions.headers) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        requestOptions.headers = {}
    }

    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    if (requestOptions.json === true) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        if (typeof requestOptions.body === 'object') {
            // Stringify the object to send as request body.
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            requestOptions.body = JSON.stringify(requestOptions.body)
        }
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        requestOptions.headers['Content-Type'] = 'application/json'
    }

    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    requestOptions.credentials = 'include'

    return requestOptions
}

/**
 * The asynchronous HTTP request.
 * @param requestPath The request path.
 * @param HTTPMethod HTTP method.
 * @param body JSON object body request body.
 * @param headers HTTP headers object.
 * @param isJSON Whether the request has JSON body.
 */
const Request = async (
    requestPath: string,
    HTTPMethod = 'GET',
    body: object | FormData | null = null,
    headers: object | null = null,
    isJSON: boolean | null = null,
    isErrorIgnored = false
    // eslint-disable-next-line
): Promise<any> => {
    const requestOptions = {
        method: HTTPMethod,
        body,
        headers,
        json: isJSON === false ? undefined : !!body,
        isErrorIgnored
    }

    // Actually send the request.
    const response = await fetch(
        `${requestPath}&ResType=JSON`,
        prepareHTTPOptions(requestOptions) as Definable<RequestInit>
    )

    // If the session has expired, redirect to login instead of surfacing the
    // 401 as an InternalAPIError. Leave the returned promise unsettled — the
    // page is about to unload.
    if (response.status === StatusCodes.UNAUTHORISED) {
        const sessionError: RESTAPIError = new RESTAPIError(response.status, null, response.statusText, null)
        if (redirectIfSessionExpired(sessionError)) {
            return new Promise<never>((): void => {
                /* never resolves — page is unloading */
            })
        }
    }

    // Process and return the response.
    return processJSONResponse(response, isErrorIgnored)
}

export default Request
